import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Carregando } from "./ui";
import AdminCatalogoVisao from "./AdminCatalogoVisao";
import AdminCatalogoProdutos from "./AdminCatalogoProdutos";
import AdminCatalogoVitrine from "./AdminCatalogoVitrine";
import { lerCatalogo, type Catalogo } from "../lib/adminCatalogo";

/**
 * Admin › Catálogo — o despacho das três frentes (000253-000257).
 *
 * ---------------------------------------------------------------------------
 * A aba com mais risco de estrago silencioso
 * ---------------------------------------------------------------------------
 * Está escrito assim em `Admin.tsx` desde 17/08, quando a aba nasceu sem tela:
 * *"mexer em id externo desvia provisionamento de venda, e mexer em card faz
 * curso perder ferramenta na tela do aluno. Precisa de confirmação e de
 * registro de quem mudou o quê."*
 *
 * As três coisas que esse aviso pede estão no desenho e nenhuma delas é da
 * tela: a CONFIRMAÇÃO é o motivo obrigatório que as RPCs cobram nos atos
 * irreversíveis, o REGISTRO é `catalogo_mudancas`, e o número que dimensiona
 * cada ato (quantos alunos perdem o quê) vem do banco na própria resposta. A
 * tela mostra; quem decide e quem recusa é o banco.
 *
 * ---------------------------------------------------------------------------
 * Três sub-abas, e a divisão é a do TRABALHO
 * ---------------------------------------------------------------------------
 *   · **Visão geral** — o índice de tudo que a casa vende e de tudo que ela
 *     entrega dentro disso. É a primeira porque é o índice do resto (000262).
 *   · **Produtos** — os 12 em 11 cursos, com a conferência dos sete lugares em
 *     que um produto vive, e a página de cada um.
 *   · **Vitrine** — o par (produto, card) e as seções: o que cada curso mostra.
 *
 * As páginas DEDICADAS — uma por produto e uma por ferramenta — moram cada uma
 * na sub-aba que lista a coisa: produto em `produtos?produto=<curso>`,
 * ferramenta em `vitrine?card=<slug>`. Não é arbitrário: duas portas para a
 * mesma tela fariam a segunda ficar desatualizada, que é a razão de este painel
 * não ter uma aba "Acessos" ao lado da de Alunos.
 *
 * ---------------------------------------------------------------------------
 * Duas abas que NÃO existem, e as duas ausências são decisão de Victor
 * ---------------------------------------------------------------------------
 * **Mudanças** (16/09). O que saiu foi a TELA: `catalogo_mudancas` continua
 * gravando cada ato — o motivo obrigatório, o antes, o depois e quem agiu —,
 * porque quem escreve o razão é o BANCO, dentro de cada RPC, e não o front.
 *
 *     node supabase/tests/sql.mjs "select * from cat_admin_mudancas(50, 0);"
 *
 * **Vendas** (21/09). Ela mostrava os três estados de um id externo e a fila do
 * que ninguém decidiu. Saiu inteira — a tela e as duas RPCs de escrita que só
 * ela chamava (`cat_admin_decidir_venda` e `cat_admin_tirar_id_externo`
 * continuam no banco, provadas pelo teste 84 e por `catalogo-no-ar.mjs`; o que
 * saiu do front foi o IO delas).
 *
 * E a fila não ficou invisível: uma venda "que ninguém decidiu" é, por
 * definição, um id externo que apareceu em evento e NÃO está no catálogo nem em
 * `ofertas_sem_concessao` — ou seja um evento que não pôde ser aplicado, com
 * `erro` gravado, que é exatamente o que a aba **Triagem** lista. O que se
 * perdeu foi o recorte por id; o fato continua tendo tela. Quem quiser o
 * recorte pergunta ao banco:
 *
 *     node supabase/tests/sql.mjs \
 *       "select cat_admin_catalogo() -> 'sem_decisao';"
 *
 * As duas estão escritas aqui, e não só no commit, porque o modo de falha de
 * uma aba removida é alguém concluir que o registro parou junto.
 *
 * A sub-aba é SUB-ROTA, não `useState`, pela mesma razão do Admin Pós: o
 * operador volta pelo botão do navegador, recarregar não o joga para a
 * primeira, e ele pode mandar o link de uma tela a um colega.
 *
 * ---------------------------------------------------------------------------
 * O catálogo é lido UMA vez, aqui
 * ---------------------------------------------------------------------------
 * São ~240 linhas ao todo, e as três telas leem o mesmo conjunto: quem desliga
 * um card na Vitrine muda o `cards` que Produtos conta. Uma leitura por sub-aba
 * criaria três fotografias do mesmo catálogo, e a que divergisse seria a que
 * ninguém está olhando. Quem recarrega é o pai, depois de todo ato.
 */

type Sub = "visao" | "produtos" | "vitrine";

const SUBS: Array<{ id: Sub; rotulo: string; nota: string }> = [
  { id: "visao", rotulo: "Visão geral", nota: "cursos e ferramentas" },
  { id: "produtos", rotulo: "Produtos", nota: "prazo, contrato e conferência" },
  // O RÓTULO é "Ferramentas" (pedido de Victor, 21/09) e o endereço continua
  // `/admin/catalogo/vitrine`: a sub-aba é SUB-ROTA justamente para o operador
  // mandar o link a um colega, e trocar o caminho quebraria o que já foi
  // mandado. O `id` também é a chave de `data-subaba`, que as provas leem.
  { id: "vitrine", rotulo: "Ferramentas", nota: "cards por curso e seções" },
];

/**
 * `podeEditar` e `veFeedbacks` vêm do nível que o BANCO calculou para esta aba
 * (`minhas_permissoes`, 000295). Com ele falso — o grupo CS, que só VÊ —, a tela
 * avisa no alto que é leitura. Os botões continuam onde estão, e é de propósito:
 * esconder cada um em treze componentes seria a segunda definição de "quem
 * edita", e a primeira (cada `cat_admin_*`) já recusa com a frase que diz como
 * pedir a permissão.
 */
export default function AdminCatalogo({
  podeEditar = true,
  veFeedbacks = false,
}: {
  podeEditar?: boolean;
  /**
   * Quem vê a aba Feedbacks, onde moram as pesquisas de satisfação desde a
   * 000332: só para ele a página do produto aponta para elas.
   */
  veFeedbacks?: boolean;
}) {
  const { sub } = useParams();
  const atual: Sub = SUBS.some((s) => s.id === sub) ? (sub as Sub) : "visao";

  const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // `negado` é separado de `erro` porque as duas mensagens são diferentes e o
  // operador precisa saber qual das duas é: "o banco recusou você" pede outra
  // conduta que "a rede falhou".
  const [negado, setNegado] = useState(false);

  const recarregar = useCallback(async () => {
    try {
      const c = await lerCatalogo();
      if (!c) {
        setNegado(true);
        setCatalogo(null);
        return;
      }
      setNegado(false);
      setErro(null);
      setCatalogo(c);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "não foi possível ler o catálogo");
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  if (negado) {
    return (
      <div className="surface rounded-cf-lg p-6" data-catalogo-negado>
        <h2 className="font-display text-lg font-bold text-titulo">O banco não devolveu o catálogo</h2>
        <p className="mt-2 text-sm text-cinza">
          Ver o catálogo exige permissão na aba <strong className="text-tinta">Catálogo</strong>, e o
          banco respondeu vazio para a sua sessão. Quem concede é um administrador, em Admin ›
          Permissões — esta tela não decide acesso, ela só mostra o que o banco entrega.
        </p>
      </div>
    );
  }

  if (erro && !catalogo) {
    return (
      <div className="surface rounded-cf-lg p-6" data-catalogo-erro>
        <h2 className="font-display text-lg font-bold text-titulo">O catálogo não veio</h2>
        <p className="mt-2 text-sm text-cinza">{erro}</p>
      </div>
    );
  }

  if (!catalogo) return <Carregando />;

  return (
    <div>
      {!podeEditar && (
        <p
          className="mb-4 rounded-cf-lg border border-alerta-tinta/40 bg-alerta-fundo px-4 py-3 text-sm text-alerta-tinta"
          data-catalogo-so-leitura
        >
          <strong>Você está vendo o Catálogo em modo leitura.</strong> Salvar, ligar ou registrar
          qualquer coisa aqui vai ser recusado. Para editar, peça a um administrador a permissão de
          edição da aba Catálogo (Admin › Permissões).
        </p>
      )}
      <nav className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="Painéis do catálogo">
        {SUBS.map((s) => (
          <Link
            key={s.id}
            to={s.id === "visao" ? "/admin/catalogo" : `/admin/catalogo/${s.id}`}
            role="tab"
            aria-selected={atual === s.id}
            data-subaba={s.id}
            className={`min-h-10 rounded-full border px-4 py-2 text-sm font-semibold transition ${
              atual === s.id
                ? "border-laranja bg-laranja text-azul"
                : "border-borda text-cinza hover:bg-chip hover:text-tinta"
            }`}
          >
            {s.rotulo}
            <span className="ml-2 hidden text-xs font-normal opacity-70 sm:inline">{s.nota}</span>
          </Link>
        ))}
      </nav>

      {atual === "visao" && <AdminCatalogoVisao catalogo={catalogo} recarregar={recarregar} />}
      {atual === "produtos" && <AdminCatalogoProdutos catalogo={catalogo} recarregar={recarregar} veFeedbacks={veFeedbacks} />}
      {atual === "vitrine" && <AdminCatalogoVitrine catalogo={catalogo} recarregar={recarregar} />}
    </div>
  );
}

/** O que cada sub-aba recebe. O catálogo é do PAI: uma leitura, três telas. */
export type PropsDoCatalogo = {
  catalogo: Catalogo;
  recarregar: () => Promise<void>;
  /** Só a sub-aba Produtos usa: a página do produto aponta para as pesquisas em Feedbacks. */
  veFeedbacks?: boolean;
};
